import { useEffect, useMemo, useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useTranslation } from 'react-i18next';
import { Icon, useSnackbar } from '@web-chat-core/ui-kit';
import { selectIsModalOpened } from 'state/selectors';
import { ModalName } from 'features/Modals/ModalProvider';
import { useModal } from 'useCase/modal/useModal';
import { useWaitingRoom } from 'features/AccessRequests/hooks/useWaitingRoom';
import { useWaitingRoomActions, WaitingRoomActionResult } from 'features/AccessRequests/hooks/useWaitingRoomActions';
import { accessRequestsSettingsStore } from 'state/store';
import {
    dismissBannerForWaitingUsers,
    shouldShowWaitingRequestsBanner,
} from 'features/AccessRequests/utils/waitingBannerDismiss';
import { getWaitingRequestsCountLabel } from 'features/AccessRequests/utils/waitingRequestsLabel';
import { getWaitingUserDisplayName } from 'features/AccessRequests/utils/getWaitingUserDisplayName';
import { WaitingUserAvatar } from 'features/AccessRequests/components/WaitingUserAvatar/WaitingUserAvatar';
import { WaitingAddParticipantsIcon, WaitingAddParticipantsIconWrapper } from '../../../Modals/ParticipantsModal/view';
import {
    BannerActions,
    BannerApproveButton,
    BannerClose,
    BannerContainer,
    BannerDescription,
    BannerLead,
    BannerPrimaryAction,
    BannerRejectButton,
    BannerSecondaryAction,
    BannerText,
    BannerTitle,
} from './view';

export const WaitingRequestsBanner = observer(() => {
    const { t, i18n } = useTranslation();
    const { showSnackbar } = useSnackbar();
    const { waitingUsers, isCanManageWaitingUsers } = useWaitingRoom();
    const { approveWaitingUser, approveAllWaitingUsers } = useWaitingRoomActions();
    const { openModal } = useModal();
    const { notificationsEnabled } = accessRequestsSettingsStore;
    const isParticipantsOpen = selectIsModalOpened(ModalName.PARTICIPANTS);
    const [isVisible, setIsVisible] = useState(false);
    const [isPending, setIsPending] = useState(false);
    const dismissedLengthRef = useRef<number | null>(null);

    const shouldShowBanner = useMemo(() => shouldShowWaitingRequestsBanner(waitingUsers), [waitingUsers]);
    const isGrouped = waitingUsers.length > 1;
    const firstUser = waitingUsers[0];
    const firstUserLabel = firstUser
        ? getWaitingUserDisplayName(firstUser) || t('web_call_unnamed_user')
        : t('web_call_unnamed_user');

    useEffect(() => {
        if (isParticipantsOpen) {
            setIsVisible(false);
            return;
        }

        if (!isCanManageWaitingUsers || !notificationsEnabled || waitingUsers.length === 0) {
            dismissedLengthRef.current = null;
            setIsVisible(false);
            return;
        }

        // Не показывать снова если количество не изменилось с момента закрытия крестиком.
        if (dismissedLengthRef.current !== null && waitingUsers.length === dismissedLengthRef.current) {
            return;
        }

        dismissedLengthRef.current = null;
        setIsVisible(shouldShowBanner);
    }, [isParticipantsOpen, isCanManageWaitingUsers, notificationsEnabled, waitingUsers.length, shouldShowBanner]);

    if (
        !isVisible ||
        !isCanManageWaitingUsers ||
        !notificationsEnabled ||
        waitingUsers.length === 0 ||
        isParticipantsOpen
    ) {
        return null;
    }

    const withPendingAction = async (action: () => Promise<WaitingRoomActionResult>, onSuccess?: () => void) => {
        if (isPending) return;
        setIsPending(true);
        try {
            const result = await action();
            if ('error' in result) {
                showSnackbar(t('web_videoconference_error'));
                return;
            }
            onSuccess?.();
        } finally {
            setIsPending(false);
        }
    };

    const handleApproveSingle = () => {
        if (!firstUser) return;
        return withPendingAction(
            () => approveWaitingUser([firstUser.waitingListId], 'APPROVE'),
            () => setIsVisible(false),
        );
    };

    const handleRejectSingle = () => {
        if (!firstUser) return;
        return withPendingAction(
            () => approveWaitingUser([firstUser.waitingListId], 'REJECT'),
            () => setIsVisible(false),
        );
    };

    const handleApproveAll = () => withPendingAction(approveAllWaitingUsers, () => setIsVisible(false));

    const handleOpenParticipants = () => {
        openModal(ModalName.PARTICIPANTS);
        setIsVisible(false);
    };

    const handleClose = () => {
        dismissedLengthRef.current = waitingUsers.length;
        dismissBannerForWaitingUsers(waitingUsers);
        setIsVisible(false);
    };

    return (
        <BannerContainer>
            {isGrouped ? (
                <>
                    <BannerLead>
                        <WaitingAddParticipantsIconWrapper>
                            <WaitingAddParticipantsIcon icon="icAddParticipants" $active={false} size={20} />
                        </WaitingAddParticipantsIconWrapper>
                        <BannerText>
                            <BannerTitle>
                                {getWaitingRequestsCountLabel(t, waitingUsers.length, i18n.language)}
                            </BannerTitle>
                            <BannerDescription>{t('vks_request_waiting_with')}</BannerDescription>
                        </BannerText>
                    </BannerLead>
                    <BannerActions>
                        <BannerPrimaryAction onClick={handleApproveAll} disabled={isPending}>
                            {t('vks_request_accept_all')}
                        </BannerPrimaryAction>
                        <BannerSecondaryAction onClick={handleOpenParticipants} disabled={isPending}>
                            {t('vks_request_view')}
                        </BannerSecondaryAction>
                    </BannerActions>
                </>
            ) : (
                <>
                    <BannerLead>
                        {firstUser && <WaitingUserAvatar user={firstUser} />}
                        <BannerText>
                            <BannerTitle>{firstUserLabel}</BannerTitle>
                            <BannerDescription>{t('vks_request_waiting_single')}</BannerDescription>
                        </BannerText>
                    </BannerLead>
                    <BannerActions>
                        <BannerApproveButton onClick={handleApproveSingle} disabled={isPending}>
                            {t('vks_request_accept')}
                        </BannerApproveButton>
                        <BannerRejectButton onClick={handleRejectSingle} disabled={isPending}>
                            {t('vks_request_reject')}
                        </BannerRejectButton>
                    </BannerActions>
                </>
            )}
            <BannerClose onClick={handleClose} size="small">
                <Icon icon="icClose" colorVariant="third" size={24} />
            </BannerClose>
        </BannerContainer>
    );
});
